import { useEffect, useMemo } from 'react';

import { Vehicle, VehicleLookupState } from '../@types';

import useGetEnquiryOffers from './useGetEnquiryOffers';
import useGetEnquiryStatus from './useGetEnquiryStatus';
import { useGetPaymentsStatuses } from './useGetPaymentsStatuses';
import { useGetLatestPostSaleOffer } from './useLatestPostSaleOffer';

type GetEnquiryStatusAndOffersProps = {
  vehicle?: Vehicle | VehicleLookupState;
}

// eslint-disable-next-line max-len
export const useGetEnquiryStatusAndOffers = ({ vehicle }: GetEnquiryStatusAndOffersProps) => {
  const dpEnquiryId = vehicle?.enquiry?.dpEnquiryId;
  const isMarketplaceEligible = Boolean(vehicle?.enquiry?.marketplaceEligible?.eligible);

  const {
    data: enquiryOffers,
    getEnquiryOffers,
    isLoading: isEnquiryOffersLoading,
  } = useGetEnquiryOffers();

  const {
    data: enquiryStateSlug,
    getEnquiryStatus,
    isLoading: isEnquiryStatusLoading,
    isOfferRestricted,
  } = useGetEnquiryStatus({ dpStateSlug: vehicle?.enquiry?.dpStateSlug });

  const {
    data: latestPostSaleOfferData,
    getLatestPostSaleOffer,
    isFetched: isLatestPostSaleOfferFetched,
  } = useGetLatestPostSaleOffer();

  const {
    data: paymentsStatuses,
    getPaymentsStatuses,
    isFetched: isPaymentsStatusesFetched,
  } = useGetPaymentsStatuses();

  useEffect(() => {
    if (!dpEnquiryId || !isMarketplaceEligible) {
      return;
    }

    getEnquiryOffers(dpEnquiryId);
    getEnquiryStatus(dpEnquiryId);
  }, [dpEnquiryId, isMarketplaceEligible, getEnquiryOffers, getEnquiryStatus]);

  useEffect(() => {
    if (!dpEnquiryId || enquiryStateSlug !== 'sold_documents_reviewed') {
      return;
    }

    getLatestPostSaleOffer(dpEnquiryId);
  }, [enquiryStateSlug, getLatestPostSaleOffer, dpEnquiryId]);

  useEffect(() => {
    if (!dpEnquiryId || latestPostSaleOfferData?.state !== 'approved') {
      return;
    }

    getPaymentsStatuses(dpEnquiryId);
  }, [getPaymentsStatuses, latestPostSaleOfferData, dpEnquiryId]);

  const isPaymentsLoading = useMemo(() => {
    // Back out if there's no enquiry (CVT)
    if (!enquiryStateSlug) {
      return false;
    }

    // Payments data isn't present till docs reviewed
    if (enquiryStateSlug !== 'sold_documents_reviewed') {
      return false;
    }

    if (!isLatestPostSaleOfferFetched) {
      return true;
    }

    // Payments statueses is not loaded if the latest post sale offer is not approved
    if (latestPostSaleOfferData?.state !== 'approved') {
      return false;
    }

    return !isPaymentsStatusesFetched;
  }, [enquiryStateSlug, isLatestPostSaleOfferFetched, latestPostSaleOfferData?.state, isPaymentsStatusesFetched]);

  const isLoading = isEnquiryOffersLoading || isEnquiryStatusLoading || isPaymentsLoading;

  return {
    enquiryOffers,
    enquiryStateSlug,
    isLoading,
    isOfferRestricted,
    latestPostSaleOfferData,
    paymentsStatuses,
  };
};
